import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, Smartphone, ShieldAlert, Check, MessageSquare, AtSign, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { BirthdayActions, BirthdayIcon } from "@/components/BirthdayAlert";

const BIRTHDAY_TYPES = ["birthday", "birthday_self", "birthday_wish"];

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();
  const load = () => api.get("/notifications").then((r) => { setItems(r.data.notifications); setUnread(r.data.unread); }).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  const forget = async (n) => {
    try {
      if (n.data?.device_id) await api.delete(`/auth/trusted-devices/${n.data.device_id}`);
      else await api.delete("/auth/trusted-devices");
      await api.post(`/notifications/${n.id}/read`);
      toast.success("Device forgotten. Consider changing your password on your Profile.");
      load();
    } catch { toast.error("Could not forget device"); }
  };
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };

  return (
    <Popover onOpenChange={(o) => o && load()}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label="Notifications" data-testid="notifications-bell">
          <Bell className="h-5 w-5" />
          {unread > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[0.6rem] font-700 text-white" data-testid="notifications-unread">{unread}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="glass w-96 p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between border-b border-[hsl(var(--gold))]/15 px-4 py-2.5">
          <p className="text-sm font-700">Alerts</p>
          {unread > 0 && <button onClick={readAll} className="text-xs text-[hsl(var(--water))]" data-testid="notifications-read-all">Mark all read</button>}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.map((n) => (
            <div key={n.id} className={`border-b border-border/50 px-4 py-3 ${n.read ? "" : "bg-[hsl(var(--gold))]/8"} ${n.type === "message" || n.type === "mention" || n.type === "access_request" ? "cursor-pointer hover:bg-secondary/60" : ""}`} data-testid={`notification-${n.id}`}
              onClick={n.type === "message" || n.type === "mention" ? async () => { if (!n.read) { await api.post(`/notifications/${n.id}/read`); load(); } navigate(`/messages?c=${n.data?.conversation_id || ""}`); }
                : n.type === "access_request" ? async () => { if (!n.read) { await api.post(`/notifications/${n.id}/read`); load(); } navigate("/command?tab=invites"); } : undefined}>
              <div className="flex items-start gap-2">
                <div className="mt-0.5 text-[hsl(var(--gold))]">{n.type === "new_device" ? <Smartphone className="h-4 w-4" /> : n.type === "message" ? <MessageSquare className="h-4 w-4" /> : n.type === "mention" ? <AtSign className="h-4 w-4" /> : n.type === "access_request" ? <UserPlus className="h-4 w-4" /> : BIRTHDAY_TYPES.includes(n.type) ? <BirthdayIcon type={n.type} /> : <ShieldAlert className="h-4 w-4" />}</div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-600">{n.title}{!n.read && <Badge className="ml-2 bg-[hsl(var(--gold))] text-[0.55rem] text-[hsl(var(--primary-foreground))]">new</Badge>}</p>
                  <p className="text-xs text-muted-foreground">{n.body}</p>
                  <p className="mt-1 text-[0.65rem] text-muted-foreground">{new Date(n.created_at).toLocaleString()}</p>
                  {n.type === "birthday" && <BirthdayActions n={n} onDone={load} />}
                  {(n.type === "birthday_self" || n.type === "birthday_wish") && (
                    <Button size="sm" variant="ghost" className="mt-1 h-7 text-xs" onClick={async () => { if (!n.read) { await api.post(`/notifications/${n.id}/read`); load(); } navigate("/dashboard"); }} data-testid={`open-mess-${n.id}`}>See it in the Mess</Button>
                  )}
                  {n.type === "new_device" && (
                    <div className="mt-2 flex gap-2">
                      <Button size="sm" variant="outline" className="h-7 border-destructive/40 text-xs text-destructive" onClick={() => forget(n)} data-testid={`forget-device-${n.id}`}>Not me — forget device</Button>
                      {!n.read && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={async () => { await api.post(`/notifications/${n.id}/read`); load(); }} data-testid={`ack-${n.id}`}><Check className="mr-1 h-3 w-3" />That was me</Button>}
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => navigate("/profile")}>Devices</Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">No alerts. You'll be told whenever a new device signs into your account.</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}
